import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, Users, Clock, Plus, Music, Ticket, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { eventsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

const GENRES = ['all', 'music', 'film', 'art', 'tech', 'fashion', 'gaming', 'community'];

export default function EventsPage() {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genre, setGenre] = useState('all');

  useEffect(() => {
    setLoading(true);
    eventsAPI.list({ genre: genre === 'all' ? '' : genre, limit: 30 })
      .then(res => setEvents(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [genre]);

  return (
    <div className="min-h-screen bg-zinc-950 pb-20" data-testid="events-page">
      <div className="bg-gradient-to-b from-zinc-900 to-zinc-950 border-b border-zinc-800">
        <div className="max-w-6xl mx-auto px-4 pt-8 pb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full">
                  <Calendar size={12} className="text-amber-400" />
                  <span className="text-amber-400 text-xs font-bold">Live Events</span>
                </span>
              </div>
              <h1 className="font-display font-bold text-3xl md:text-4xl text-white">Events & Tickets</h1>
              <p className="text-zinc-500 text-sm mt-1">Live experiences from creators you love. Secure QR tickets.</p>
            </div>
            {user && (user.role === 'creator' || user.role === 'vendor') && (
              <Link to="/events/create">
                <Button className="bg-amber-500 text-black font-bold rounded-full" data-testid="create-event-btn">
                  <Plus size={16} className="mr-1" /> Create Event
                </Button>
              </Link>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {GENRES.map(g => (
              <button key={g} onClick={() => setGenre(g)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${genre === g ? 'bg-amber-500 text-black' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'}`}
                data-testid={`event-genre-${g}`}
              >{g === 'all' ? 'All' : g.charAt(0).toUpperCase() + g.slice(1)}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 mt-6">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => <div key={i} className="h-48 bg-zinc-900 rounded-2xl animate-pulse" />)}
          </div>
        ) : events.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event, idx) => (
              <motion.div key={event.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}>
                <Link to={`/events/${event.id}`} className="block rounded-2xl overflow-hidden bg-zinc-900 border border-zinc-800 hover:border-amber-500/40 group transition-all" data-testid={`event-card-${event.id}`}>
                  <div className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs font-bold text-zinc-500 uppercase tracking-wider">{event.genre}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${event.status === 'upcoming' ? 'bg-[#b8ff00]/10 text-[#b8ff00]' : event.status === 'completed' ? 'bg-zinc-700 text-zinc-400' : 'bg-red-500/10 text-red-400'}`}>
                        {event.status}
                      </span>
                    </div>
                    <h3 className="font-display font-bold text-lg text-white mb-2 group-hover:text-amber-400 transition-colors">{event.title}</h3>
                    <p className="text-xs text-zinc-500 mb-3">by {event.creator_name}</p>

                    <div className="space-y-2 text-sm text-zinc-400">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-amber-400" />
                        <span>{event.event_date} at {event.event_time}</span>
                      </div>
                      {event.venue && (
                        <div className="flex items-center gap-2">
                          <MapPin size={14} className="text-amber-400" />
                          <span>{event.venue}{event.location ? `, ${event.location}` : ''}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <Ticket size={14} className="text-amber-400" />
                        <span>{event.total_tickets_sold || 0} tickets sold</span>
                      </div>
                    </div>

                    {/* Ticket pricing */}
                    {event.ticket_tiers?.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-zinc-800 flex items-center justify-between">
                        <span className="text-xs text-zinc-500">From <span className="text-amber-400 font-bold">${Math.min(...event.ticket_tiers.map(t => t.price))}</span></span>
                        <span className="text-xs font-semibold text-amber-400 flex items-center gap-1 group-hover:translate-x-1 transition-transform">Get tickets <ChevronRight size={12} /></span>
                      </div>
                    )}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-zinc-900 rounded-2xl border border-zinc-800">
            <Music size={48} className="mx-auto text-zinc-700 mb-4" />
            <p className="text-zinc-500 mb-4">No events yet.</p>
            {user && (
              <Link to="/events/create">
                <Button className="bg-amber-500 text-black font-bold rounded-full">Create an Event</Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
